/**
 * unitConversion.js
 *
 * Dimension-aware unit conversion for number coloring schemas. A schema stores its
 * bands in ONE canonical unit (Remaining Life bands are in MONTHS); a widget binding
 * declares the unit its measure is actually in (e.g. years). The evaluator converts the
 * value into the schema unit BEFORE bucketing, so -0.6 years buckets as -7.2 months, not
 * a naive -0.6 (COLORING_SCHEMA_RESEARCH.md §4.6). An unconvertible pair (cross-dimension
 * or an unknown unit) returns null so the caller surfaces `schemaUnavailable` (§4.4),
 * never a silent mis-bucket.
 *
 * Pure leaf module: no store, no component, no other project imports.
 */

// Canonical unit names referenced by the built-in schemas and widget bindings. COUNT is the
// dimensionless unit for unitless measures (raw counts) — an honest alternative to persisting a
// placeholder length unit; it converts only to itself.
export const UNIT = Object.freeze({
  MM_PER_YEAR: 'mm/yr',
  MONTHS: 'months',
  YEARS: 'years',
  MM: 'mm',
  CM: 'cm',
  M: 'm',
  INCH: 'in',
  COUNT: 'count',
});

// Each known unit maps to { dim, factor }; factor converts the unit to its dimension's
// canonical base (time→months, length→mm, rate→mm/yr). Same-dimension conversion is
// value * factor[from] / factor[to].
const UNIT_FACTORS = Object.freeze({
  // time — base: months
  months: Object.freeze({ dim: 'time', factor: 1 }),
  years: Object.freeze({ dim: 'time', factor: 12 }),
  // length — base: mm
  mm: Object.freeze({ dim: 'length', factor: 1 }),
  cm: Object.freeze({ dim: 'length', factor: 10 }),
  m: Object.freeze({ dim: 'length', factor: 1000 }),
  in: Object.freeze({ dim: 'length', factor: 25.4 }),
  // corrosion rate — base: mm/yr
  'mm/yr': Object.freeze({ dim: 'rate', factor: 1 }),
  // dimensionless — unitless counts; its own dimension, so it converts only to itself and
  // canConvert(count, <any other unit>) is false.
  count: Object.freeze({ dim: 'dimensionless', factor: 1 }),
});

// Case/spacing/plural variants normalize to a UNIT_FACTORS key.
const UNIT_ALIASES = Object.freeze({
  month: 'months', mo: 'months', mos: 'months',
  year: 'years', yr: 'years', yrs: 'years', y: 'years',
  millimeter: 'mm', millimetre: 'mm', millimeters: 'mm', millimetres: 'mm',
  centimeter: 'cm', centimetre: 'cm',
  meter: 'm', metre: 'm', meters: 'm', metres: 'm',
  inch: 'in', inches: 'in',
  'mm/year': 'mm/yr', mmpy: 'mm/yr', 'mm/y': 'mm/yr',
  counts: 'count',
});

// A raw unit string → a canonical UNIT_FACTORS key, or null when unknown. Exported so the engine can
// ask "is this unit convert-known?" and treat an unknown (free-text) unit as a display-only,
// dimensionless label instead of a hard failure (§4.6 relaxation).
export function normalizeUnit(unit) {
  if (unit == null) return null;
  const key = String(unit).trim().toLowerCase();
  if (key === '') return null;
  if (Object.prototype.hasOwnProperty.call(UNIT_FACTORS, key)) return key;
  if (Object.prototype.hasOwnProperty.call(UNIT_ALIASES, key)) return UNIT_ALIASES[key];
  return null;
}

/**
 * True when `fromUnit` can be converted into `toUnit` (same dimension, both known). An
 * absent `fromUnit` is treated as "already in the schema unit" and is always convertible.
 * @param {string|null|undefined} fromUnit
 * @param {string} toUnit
 * @returns {boolean}
 */
export function canConvert(fromUnit, toUnit) {
  const to = normalizeUnit(toUnit);
  if (to == null) return false;
  if (fromUnit == null) return true;
  const from = normalizeUnit(fromUnit);
  if (from == null) return false;
  return UNIT_FACTORS[from].dim === UNIT_FACTORS[to].dim;
}

/**
 * Convert `value` from `fromUnit` into `toUnit`. Returns a number, or null when the pair
 * is unconvertible (unknown unit, or a cross-dimension pair). A null/absent `fromUnit`
 * means "already in the schema unit" and passes the value through unchanged.
 * @param {number} value
 * @param {string|null|undefined} fromUnit
 * @param {string} toUnit
 * @returns {number|null}
 */
export function convertUnit(value, fromUnit, toUnit) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  // Same-unit passthrough BEFORE the known-unit gate: identical (trimmed, case-insensitive) units
  // need no conversion, so a free-text/unknown unit like "psi"→"psi" returns the value unchanged
  // rather than null. Genuinely DIFFERENT unknown pairs still return null (the null-on-unknown
  // contract is unchanged), so a caller that must distinguish them still can.
  if (fromUnit != null && String(fromUnit).trim().toLowerCase() === String(toUnit).trim().toLowerCase()) {
    return value;
  }
  const to = normalizeUnit(toUnit);
  if (to == null) return null;
  if (fromUnit == null) return value;
  const from = normalizeUnit(fromUnit);
  if (from == null) return null;
  if (from === to) return value;
  const f = UNIT_FACTORS[from];
  const t = UNIT_FACTORS[to];
  if (f.dim !== t.dim) return null;
  return (value * f.factor) / t.factor;
}
